import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy,
  Users,
  BarChart3,
  Menu,
  X,
  Sparkles,
  Home,
  Award,
  Calendar,
  TrendingUp,
  UserCircle,
  Briefcase,
  Shield
} from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';
import { useSiteConfig } from '../../hooks/useSiteConfig';
// import LoginButton from '../Auth/LoginButton';

const Header: React.FC = () => {
  const location = useLocation();
  const site = useSite();
  const siteConfig = useSiteConfig();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check for admin access (you can customize this logic)
    // Option 1: Check URL parameter
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === 'true') {
      localStorage.setItem('adminAccess', 'true');
      setIsAdmin(true);
    }
    // Option 2: Check localStorage
    else if (localStorage.getItem('adminAccess') === 'true') {
      setIsAdmin(true);
    }
  }, []);

  const navItems = [
    { path: '/scores', label: 'SCORES', icon: Trophy },
    { path: '/seasons', label: 'SEASONS', icon: Calendar },
    { path: '/standings', label: 'STANDINGS', icon: TrendingUp },
    { path: '/teams', label: 'TEAMS', icon: Users },
    { path: '/players', label: 'PLAYERS', icon: UserCircle },
    { path: '/coaches', label: 'COACHES', icon: Briefcase },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'backdrop-blur-xl bg-background/80 border-b border-border/50' 
            : 'backdrop-blur-sm bg-background/40'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              {siteConfig.logoUrl ? (
                // Use custom logo if available
                <img 
                  src={siteConfig.logoUrl}
                  alt={siteConfig.name}
                  className="h-10 md:h-12 w-auto max-w-[320px] object-contain transition-transform group-hover:scale-105"
                  onError={(e) => {
                    // Fallback to text logo if image fails to load
                    console.error('Logo failed to load:', siteConfig.logoUrl);
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              {/* Fallback text logo (hidden by default if logoUrl exists) */}
              <div className={`flex items-center space-x-2 ${siteConfig.logoUrl ? 'hidden' : ''}`}>
                <div className="relative p-1">
                  <div className="absolute inset-0 bg-primary-500/20 blur-lg group-hover:bg-primary-500/30 transition-colors rounded-full" />
                  <Trophy className="relative w-8 h-8 text-primary-500" />
                </div>
                <div className="flex flex-col">
                  {siteConfig.sport ? (
                    // Child site - single line name
                    <>
                      <h1 className="text-xl font-black leading-tight">
                        <span className="text-primary-500">{siteConfig.name}</span>
                      </h1>
                      <p className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-medium">
                        Championship Analytics
                      </p>
                    </>
                  ) : (
                    // Main site - two line name
                    <>
                      <h1 className="text-lg font-black leading-tight">
                        <span className="text-primary-500">The Sports</span>
                      </h1>
                      <h1 className="text-lg font-black leading-tight -mt-1">
                        <span className="text-white">Machine</span>
                      </h1>
                      <p className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-medium">
                        Championship Analytics
                      </p>
                    </>
                  )}
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {/* Admin Link - Only visible for admin users */}
              {isAdmin && (
                <a
                  href="http://localhost:8022/admin/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative px-4 py-2 rounded-lg transition-all duration-300 flex items-center space-x-2 text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                  title="Django Admin"
                >
                  <Shield className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-wider">ADMIN</span>
                  <div className="absolute inset-0 rounded-lg bg-amber-500/0 group-hover:bg-amber-500/5 transition-colors" />
                </a>
              )}
              
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="relative group px-4 py-2"
                  >
                    <div className={`
                      flex items-center space-x-2 relative z-10
                      transition-all duration-300
                      ${active 
                        ? 'text-primary-500' 
                        : 'text-gray-400 hover:text-white'
                      }
                    `}>
                      <Icon className="w-4 h-4" />
                      <span className="font-semibold text-xs tracking-wider">
                        {item.label}
                      </span>
                    </div>
                    
                    {/* Active indicator */}
                    {active && (
                      <motion.div 
                        layoutId="activeNav"
                        className="absolute inset-0 rounded-lg"
                        initial={false}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      >
                        <div className="absolute inset-0 bg-primary/10 rounded-lg" />
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                      </motion.div>
                    )}
                    
                    {/* Hover effect */}
                    {!active && (
                      <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/30" />
                      </div>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* CTA Button and Login */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/analytics"
                className="flex items-center space-x-2 px-6 py-2 text-xs font-bold tracking-wider text-primary border border-primary rounded-lg hover:bg-primary hover:text-black transition-all duration-300 group"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>EXPLORE DATA</span>
              </Link>
              {/* <LoginButton /> */}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-5 h-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-5 h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl"
            >
              <nav className="px-4 py-4 space-y-1">
                {navItems.map((item, index) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  
                  return (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`
                          flex items-center space-x-3 px-4 py-3 rounded-lg
                          transition-all duration-300
                          ${active 
                            ? 'bg-primary/10 text-primary' 
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                          }
                        `}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-semibold text-sm">{item.label}</span>
                      </Link>
                    </motion.div>
                  );
                })}
                
                {/* Mobile Explore Data Button */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navItems.length * 0.05 }}
                  className="mt-4 pt-4 border-t border-border/50"
                >
                  <Link
                    to="/analytics"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="mx-4 flex items-center justify-center space-x-2 px-6 py-3 text-xs font-bold tracking-wider text-primary border border-primary rounded-lg hover:bg-primary hover:text-black transition-all duration-300"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>EXPLORE DATA</span>
                  </Link>
                </motion.div>

                {/* Mobile Login Button */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (navItems.length + 1) * 0.05 }}
                  className="mt-2"
                >
                  <div className="px-4">
                    {/* <LoginButton /> */}
                  </div>
                </motion.div>

                {/* Mobile Admin Link */}
                {isAdmin && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: (navItems.length + 1) * 0.05 }}
                    className="mt-4 pt-4 border-t border-border/50"
                  >
                    <a
                      href="http://localhost:8022/admin/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-amber-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all duration-300"
                    >
                      <Shield className="w-4 h-4" />
                      <span className="font-semibold text-sm">DJANGO ADMIN</span>
                    </a>
                  </motion.div>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
      
      {/* Spacer for fixed header */}
      <div className="h-16" />
    </>
  );
};

export default Header;